import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as BackArrowIcon } from 'assets/iconButtons/back-arrow-icon.svg';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/PopUpScheme/ConfirmationModal';
import SchemaForm from 'components/SchemaForm';
import { validateBySchema } from 'components/SchemaForm/schemaUtils';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import { INTEGRATION_SETTING_SOURCE } from 'stores/integrationSettingsStore';
import Button from 'uikit/Button/Button';
import IntegrationSettingSourceBadge from 'pages/Settings/components/IntegrationSettingSourceBadge';
import { getProviderLabel, getSettingChangeText, getSettingLabel } from 'pages/Settings/integrationSettingLabels';
import './IntegrationSettingDetails.scss';

const IntegrationSettingDetails = ({ integrationSettingsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const { provider, settingKey } = useParams();
  const [isValidationShown, setIsValidationShown] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const { setting, draftValue, serverErrors, isSaving, isVersionConflict, isDraftChanged } = integrationSettingsStore;

  useEffect(() => {
    integrationSettingsStore.getIntegrationSetting(provider, settingKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, settingKey]);

  // Errors of the previous value must not show up right after a reload or a save
  useEffect(() => {
    setIsValidationShown(false);
  }, [setting]);

  const validationErrors = useMemo(
    () => (setting ? validateBySchema(setting.schema, draftValue) : {}),
    [setting, draftValue]
  );

  // The path names the provider and the setting, so the page needs no separate title
  const breadcrumbs = (
    <nav className={'integration-setting-breadcrumbs'}>
      <button
        type={'button'}
        className={'integration-setting-back-button'}
        onClick={() => navigate(ROUTE_PATHS.SETTINGS_INTEGRATIONS)}
      >
        <BackArrowIcon />
        {i18n.getMessage('settings.integrations.button.back')}
      </button>
      <span className={'integration-setting-breadcrumb'} title={provider}>
        {getProviderLabel(i18n, provider)}
      </span>
      <span className={'integration-setting-breadcrumb'} title={settingKey} aria-current={'page'}>
        {getSettingLabel(settingKey)}
      </span>
    </nav>
  );

  const isSettingLoaded = Boolean(setting) && integrationSettingsStore.isCurrentSetting(provider, settingKey);

  if (!isSettingLoaded && !integrationSettingsStore.settingError) {
    return <Loader className={'application-loader'} />;
  }

  if (!isSettingLoaded) {
    return (
      <div className={'integration-setting-wrapper'}>
        {breadcrumbs}
        <EmptyTableContentPlug
          headerText={i18n.getMessage('settings.integrations.notFound.header')}
          descriptionText={i18n.getMessage('settings.integrations.notFound.description')}
        />
      </div>
    );
  }

  const isOnDefaults = setting.source === INTEGRATION_SETTING_SOURCE.DEFAULTS;
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const formErrors = isValidationShown ? { ...validationErrors, ...serverErrors } : serverErrors;

  const handleSave = () => {
    if (hasValidationErrors) {
      setIsValidationShown(true);
      return;
    }
    integrationSettingsStore.updateIntegrationSetting();
  };

  const handleReset = async () => {
    await integrationSettingsStore.resetIntegrationSetting();
    setIsResetModalOpen(false);
  };

  return (
    <div className={'integration-setting-wrapper'}>
      <div className={'integration-setting-header'}>
        {breadcrumbs}
        <div className={'integration-setting-meta'}>
          <IntegrationSettingSourceBadge source={setting.source} />
          <span className={'integration-setting-meta-item'}>
            {i18n.getMessage('settings.integrations.meta.versionShort', { version: setting.version })}
          </span>
          <span className={'integration-setting-meta-item'}>
            {getSettingChangeText(i18n, setting, integrationSettingsStore.getOperatorName)}
          </span>
        </div>
        {setting.description && <p className={'integration-setting-description'}>{setting.description}</p>}
      </div>

      {isVersionConflict && (
        <div className={'integration-setting-conflict'}>
          <p>{i18n.getMessage('settings.integrations.conflict.text')}</p>
          <Button
            type={'outline'}
            size={'small'}
            onClick={() => integrationSettingsStore.getIntegrationSetting(provider, settingKey)}
          >
            {i18n.getMessage('settings.integrations.conflict.button.reload')}
          </Button>
        </div>
      )}

      <SchemaForm
        schema={setting.schema}
        value={draftValue}
        errors={formErrors}
        onChange={integrationSettingsStore.setDraftValue}
        isDisabled={isSaving}
      />

      <div className={'integration-setting-actions'}>
        <Button
          type={'primary'}
          size={'medium'}
          onClick={handleSave}
          isDisabled={!isDraftChanged || isSaving || isVersionConflict}
        >
          {isSaving ? <Loader /> : i18n.getMessage('settings.integrations.button.save')}
        </Button>
        <Button
          type={'outline'}
          size={'medium'}
          onClick={integrationSettingsStore.discardChanges}
          isDisabled={!isDraftChanged || isSaving}
        >
          {i18n.getMessage('settings.integrations.button.discard')}
        </Button>
        <Button
          className={'integration-setting-reset-button'}
          type={'danger'}
          size={'medium'}
          onClick={() => setIsResetModalOpen(true)}
          isDisabled={isOnDefaults || isSaving}
        >
          {i18n.getMessage('settings.integrations.button.reset')}
        </Button>
      </div>

      {!isOnDefaults && (
        <details className={'integration-setting-defaults'}>
          <summary>{i18n.getMessage('settings.integrations.defaults.title')}</summary>
          <SchemaForm
            schema={setting.schema}
            value={setting.defaults}
            onChange={() => {}}
            isReadOnly
            idPrefix={'integration-setting-defaults'}
          />
        </details>
      )}

      <ConfirmationModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={handleReset}
        isLoading={isSaving}
        type={'delete'}
        title={i18n.getMessage('settings.integrations.resetModal.title')}
        description={i18n.getMessage('settings.integrations.resetModal.description')}
        cancelButtonText={i18n.getMessage('settings.integrations.resetModal.button.cancel')}
        confirmButtonText={i18n.getMessage('settings.integrations.resetModal.button.confirm')}
      />
    </div>
  );
};

IntegrationSettingDetails.propTypes = {
  integrationSettingsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  integrationSettingsStore: stores.integrationSettingsStore
}))(observer(IntegrationSettingDetails));
